/*
 * The one reducer. `applyEvent(model, event)` is pure: no I/O, no fetch, no timer — it returns the
 * next model and what it did, and the caller (store.ts) acts on the result. Object identity is
 * preserved for everything an event does not touch (REUSE C5), which is what keeps memoised rows
 * from re-rendering.
 *
 * Two halves. The gate is the client half of the catch-up contract (protocol.md §2.8, the
 * pseudocode there is normative): duplicates are dropped by `eventId`, and for a seq-allocating
 * event by `seq`; a hole in the dense per-conversation seq is a lost frame and asks for a refetch;
 * an event for a conversation whose refetch is in flight is held or dropped. The reducers are the
 * core cases; a module's "<id>.*" event goes to the reducer that module registered, and a type
 * nobody handles is "unknown" — warned once per session, never applied (protocol.md §2.4).
 *
 * The documents' `doc.*` events are the one case that is not a reduction: they change no slice, and
 * `docs/live.ts` hands each to the page that has its document open (DV S-12 §3.3). The model comes
 * back as it went in; a durable one still moves the cursor.
 */

import { reduceDoc } from "../docs/live.ts"
import { isDocEvent } from "../docs/wire/events.ts"
import type { EventReducer } from "../modules/registry.ts"
import { isCoreEvent, type CoreEvent, type WireEvent } from "../wire/index.ts"
import type {
	Applied,
	ConversationId,
	ConversationSlice,
	MessageSlice,
	MessageView,
	Model,
	PublicUser,
	SelfUser,
} from "./model.ts"

/** Finds the reducer of the module that owns an event type — `ModuleRegistry.reducerFor`. */
export type ReducerLookup = (type: string) => EventReducer | undefined

export interface ApplyOptions {
	/**
	 * True while the store replays an event the gate already accepted and held: the cursor moved
	 * past it while it waited, so the `eventId` rule must not take it for a duplicate.
	 */
	replay?: boolean
}

const warnedTypes = new Set<string>()

/** Forgets which unknown types were warned about; for tests that assert the once-per-session rule. */
export function resetUnknownTypeWarnings(): void {
	warnedTypes.clear()
}

export function applyEvent(
	model: Model,
	event: WireEvent,
	moduleReducer?: ReducerLookup,
	options: ApplyOptions = {},
): { next: Model; result: Applied } {
	if (!options.replay && event.eventId !== undefined && event.eventId <= model.meta.lastEventId) {
		return { next: model, result: "duplicate" }
	}

	let gated = model
	if (event.conv !== undefined) {
		const verdict = gate(model, event.conv, event.seq)
		if (verdict.result === "duplicate") return { next: model, result: "duplicate" }
		if (verdict.result !== "pass") {
			// Held or handed to a refetch: the frame was consumed in order, so the cursor moves on
			// (a replay skips the eventId rule for exactly this reason).
			return { next: advanceCursor(verdict.next, event.eventId), result: verdict.result }
		}
		gated = verdict.next
	}

	let reduced: { next: Model; result: Applied }
	if (isCoreEvent(event)) {
		reduced = reduceCore(gated, event)
	} else if (isDocEvent(event)) {
		reduced = reduceDoc(gated, event)
	} else {
		const reducer = moduleReducer?.(event.type)
		if (!reducer) {
			warnUnknownType(event.type)
			return { next: model, result: "unknown" }
		}
		reduced = reducer(gated, event)
	}

	if (reduced.result === "duplicate" || reduced.result === "unknown") return reduced
	return { next: advanceCursor(reduced.next, event.eventId), result: reduced.result }
}

// ---- the gate ----

type Verdict = { next: Model; result: Applied | "pass" }

/**
 * The gap rule (spec 12 §3.4, protocol.md §2.8). A seq-allocating event — `seq` on the envelope —
 * is checked against the dense per-conversation counter; a reference event (`conv` but no `seq`)
 * is never gap-checked, because it carries the message's seq inside `data` and applies whole.
 *
 * A conversation the model does not hold yet: a seq event is held until the membership that
 * introduces it arrives, a reference event passes so that the reducer creating the slice sees it.
 * `loading` holds both kinds — the tail fetch in flight settles them; `resyncPending` drops a seq
 * event (the refetch contains it) and holds a reference event, which is safe to replay after the
 * page because events travel whole (protocol.md §2.4). A cold conversation applies: nobody is
 * looking at it, but its sidebar entry — `lastSeq`, the unread count — moves on every message,
 * and a hole there costs one bounded tail fetch.
 */
function gate(model: Model, conv: ConversationId, seq: number | undefined): Verdict {
	const slice = model.conversations[conv]
	if (seq === undefined) {
		if (slice && (slice.status === "loading" || slice.status === "resyncPending")) {
			return { next: model, result: "buffered" }
		}
		return { next: model, result: "pass" }
	}
	if (!slice || slice.status === "loading") return { next: model, result: "buffered" }
	if (slice.status === "resyncPending") return { next: model, result: "duplicate" }
	if (seq <= slice.lastSeq) return { next: model, result: "duplicate" }
	if (slice.justJoined) {
		const settled = { ...slice }
		delete settled.justJoined
		return { next: withConversation(model, conv, settled), result: "pass" }
	}
	if (seq !== slice.lastSeq + 1) {
		return {
			next: withConversation(model, conv, { ...slice, status: "resyncPending" }),
			result: { resync: "conv", conv },
		}
	}
	return { next: model, result: "pass" }
}

function advanceCursor(model: Model, eventId: number | undefined): Model {
	if (eventId === undefined || eventId <= model.meta.lastEventId) return model
	return { ...model, meta: { ...model.meta, lastEventId: eventId } }
}

function warnUnknownType(type: string): void {
	if (warnedTypes.has(type)) return
	warnedTypes.add(type)
	console.warn(`Ignoring event of unknown type "${type}".`)
}

// ---- the core reducers ----

/**
 * Exhaustive over `CoreEvent`: a member added to the union without a case here is a compile
 * error. Events that change nothing in the model — a hint the platform layer turns into a
 * notification, an instance setting the admin page refetches — are "applied" so the cursor moves.
 */
function reduceCore(model: Model, event: CoreEvent): { next: Model; result: Applied } {
	switch (event.type) {
		case "rt.resync":
			return reduceResync(model, event.data.scope, event.data.convs ?? [])
		case "rt.draining":
			return applied({ ...model, connection: "draining" })
		case "session.revoked":
			return applied({ ...model, connection: "revoked" })
		case "account.updated": {
			const user = event.data.user
			const users = { ...model.users, [user.id]: user }
			const me = model.me && model.me.id === user.id ? ownRow(model.me, user) : model.me
			return applied({ ...model, users, me })
		}
		case "account.group.changed": {
			const group = event.data.group
			const groups = { ...model.groups }
			if (group.deletedAt !== undefined) delete groups[group.id]
			else groups[group.id] = group
			return applied({ ...model, groups })
		}
		case "policy.changed":
			// What this account may see has changed; the whole model is rebuilt from a fresh
			// bootstrap rather than patched (protocol.md §2.11).
			return { next: model, result: { resync: "all" } }
		case "settings.changed":
			return applied(model)
		case "ui.settings.changed":
			return applied({ ...model, settings: { ...model.settings, ...event.data.settings } })
		case "activity.item":
			// The badge counts rows, not messages: a delivery that only raised the count of a row
			// already in the feed says so (`new: false`), and a second direct message must not
			// raise the number twice.
			//
			// The flag is read as "not false" rather than as "true", because the change log replays
			// payloads written before the flag existed (protocol.md §6): an item without it was
			// written when every delivery was a new row, and dropping its increment would leave a
			// replayed feed under-counted.
			return applied(
				event.data.new !== false && event.data.item.readAt === undefined
					? { ...model, activityUnread: model.activityUnread + 1 }
					: model,
			)
		case "activity.read": {
			const ids = event.data.ids
			if (!ids || ids.length === 0) return applied(model)
			return applied({ ...model, activityUnread: Math.max(0, model.activityUnread - ids.length) })
		}
		case "notify.hint":
			return applied(model)
		case "notify.prefs.changed":
			return applied({ ...model, notificationPrefs: event.data })
		case "file.scanChanged":
			return applied(
				withScanState(model, event.data.conv, event.data.attachmentIds, event.data.scanState),
			)
		default: {
			const exhaustive: never = event
			return exhaustive
		}
	}
}

function applied(next: Model): { next: Model; result: Applied } {
	return { next, result: "applied" }
}

/**
 * The owner's own row after an `account.updated` about them.
 *
 * The event carries the **complete** public view of the account and never a patch of what moved
 * (`internal/core/wire/events.go`), which is why `users` is replaced whole above. So the same rule
 * holds here: every public field is taken from the payload, and an optional one the payload does
 * not name is empty — a picture that was removed, a title that was cleared — not unchanged. A
 * merge that kept the old value would be a second, contradictory rule, and it would make removing
 * one's own photo invisible in every other tab.
 *
 * Only the fields no other rung receives are kept from the row the client already holds, and those
 * are the ones spelled out: `SelfUser` requires each of them, so a self field added to the type and
 * forgotten here does not compile, while a public field added to the payload needs no line at all.
 */
function ownRow(me: SelfUser, user: PublicUser): SelfUser {
	return {
		...user,
		email: me.email,
		locale: me.locale,
		// The zone rides on every event, being a public field from the member rung up; the fallback
		// covers a payload projected below that rung, which would otherwise leave the owner without
		// the zone every date on the page is formatted in.
		timezone: user.timezone ?? me.timezone,
		stepUp: me.stepUp,
		hasPassword: me.hasPassword,
	}
}

/**
 * A resync the server ordered. Every named conversation is marked before anything is fetched —
 * the mark is what makes the events arriving meanwhile drop instead of interleaving with the page
 * (REUSE C5) — and the store starts one fetch per marked conversation.
 */
function reduceResync(
	model: Model,
	scope: string,
	convs: number[],
): { next: Model; result: Applied } {
	if (scope !== "conv") return { next: model, result: { resync: "all" } }
	let next = model
	for (const conv of convs) {
		const slice = next.conversations[conv]
		if (slice && slice.status !== "resyncPending") {
			next = withConversation(next, conv, { ...slice, status: "resyncPending" })
		}
	}
	const first = convs[0]
	return { next, result: first === undefined ? "applied" : { resync: "conv", conv: first } }
}

/** The scan state of attachments in a loaded window; rows without one of the ids keep identity. */
function withScanState(
	model: Model,
	conv: ConversationId,
	attachmentIds: number[],
	scanState: string,
): Model {
	const window = model.messages[conv]
	if (!window) return model
	const ids = new Set(attachmentIds)
	let changed = false
	const rows = window.rows.map((row) => {
		if (!row.attachments?.some((a) => ids.has(a.id))) return row
		changed = true
		const attachments = row.attachments.map((a) => (ids.has(a.id) ? { ...a, scanState } : a))
		return { ...row, attachments } satisfies MessageView
	})
	if (!changed) return model
	return withMessages(model, conv, { ...window, rows })
}

// ---- identity-preserving updates, shared with the store ----

export function withConversation(
	model: Model,
	conv: ConversationId,
	slice: ConversationSlice,
): Model {
	return { ...model, conversations: { ...model.conversations, [conv]: slice } }
}

export function withMessages(model: Model, conv: ConversationId, window: MessageSlice): Model {
	return { ...model, messages: { ...model.messages, [conv]: window } }
}
